/**
 * Online status and data freshness (CAP-4 offline behaviour). Headless
 * stores; the browser globals are injected so tests run without a DOM.
 */

export interface OnlineTarget {
  navigator: { onLine: boolean };
  addEventListener(type: "online" | "offline", cb: () => void): void;
  removeEventListener(type: "online" | "offline", cb: () => void): void;
}

export interface OnlineStatus {
  isOnline(): boolean;
  subscribe(cb: () => void): () => void;
}

export function createOnlineStatus(target: OnlineTarget): OnlineStatus {
  return {
    isOnline: () => target.navigator.onLine,
    subscribe(cb) {
      target.addEventListener("online", cb);
      target.addEventListener("offline", cb);
      return () => {
        target.removeEventListener("online", cb);
        target.removeEventListener("offline", cb);
      };
    },
  };
}

/** Time of the last successful API response ("Last updated …" offline). */
export interface Freshness {
  lastUpdated(): number | null;
  markFresh(at?: number): void;
  subscribe(cb: () => void): () => void;
}

export function createFreshness(now: () => number = Date.now): Freshness {
  let last: number | null = null;
  const subs = new Set<() => void>();
  return {
    lastUpdated: () => last,
    markFresh(at = now()) {
      last = at;
      for (const s of subs) s();
    },
    subscribe(cb) {
      subs.add(cb);
      return () => {
        subs.delete(cb);
      };
    },
  };
}
